// The `?selected_tab` legacy vocabulary (ORP-19899, cutover precondition 3 of ORP-18893).
//
// v3 deep-linked into settings with `?selected_tab=<Tab>` against ONE route whose panes were
// `v-show` siblings. v4 has real child routes, so a legacy URL has to be translated. This module
// owns the translation because the vocabulary is module 22's — `app/shell` only registers it,
// exactly as `registerReportQueryCallbacks` registers `features/reports`' own id→route map.
//
// ── What is actually left to shim (measured, not inherited from the ticket) ──
// v3 retired the param itself in `7ec0fead` (2026-07-17, "migrate tab state from store to URL-based
// routing"): `origin/main` has ZERO producers. v4 has exactly one — `SummaryEmptyState.vue:16`,
// whose "connect your first data source" CTA lands on Workspace Basics today. So this shim serves
// that CTA plus externally-held legacy URLs (bookmarks, mails, support macros), NOT live v3 traffic.
//
// ── Why nav KEYS and not route names ──
// A route name that no registrant has declared makes `router.replace` throw, and the seam's
// try/catch wraps only the handler call, not the redirect (`useQueryCallbacks.ts:150-162`) — the
// rejection would escape through `void consume(...)`. Resolving a key against the live registry
// instead means an unregistered pane simply yields `undefined`, and the moment its owner registers
// (12's Connections, ORP-19897) the same table starts resolving with no change here.
import { orderedVisibleSettingsEntries } from '../composables/useSettingsNav'

import { settingsNavEntries } from './settingsNavRegistry'

/**
 * Legacy tab token → settings-nav key. Keys are lowercase because v3 matched case-insensitively
 * against BOTH the item `name` and its `value` (`views/App/WorkspaceSettings.vue:68-90` at
 * `7ec0fead^`); for every tab v4 has, those two collapse to the same token ('Workspace'/'workspace').
 *
 * `Workspace` was a PARENT in v3 and resolved to its first child, Basics — hence both tokens here.
 *
 * `Workspace` was a PARENT in v3 and resolved to its first child, Basics — hence both tokens here.
 *
 * ⚠ EVERY tab carries its PATH-SEGMENT alias too, not just its display label. `toUrlSegment()`
 * turns an item's `value` into the URL token by swapping `_` for `-`
 * (`useWorkspaceSettings.js:9-11`), and `legacySettings.ts` feeds this table the last segment of a
 * legacy path — so `/workspace-settings/workspace/custom-definitions` arrives as
 * `custom-definitions`, which the display label `custom definitions` does not match. Before
 * ORP-20208 five of v3's segments resolved to nothing and silently took the fallback below.
 *
 * ⚠ `subscription` is the CREDITS pane, not a missing one. An earlier revision left it unmapped on
 * the reading that v3 had commented the tab out; re-measured on v3 `origin/main`, the entry is live
 * at `useWorkspaceSettings.js:212-217` (`active: true`) and its pane is `CreditSettings.vue`
 * (`views/App/WorkspaceSettings.vue:38`) — the same pane `credits` resolves to. v3 treats
 * `subscription` as the canonical token and redirects the other two spellings onto it
 * (`router/routes/app.js:269` for `/credit-settings`, `:412-415` for `/workspace-settings/credits`).
 *
 * ⚠ `insights` is 15's SYSADMIN workbench (`WorkspaceInsights.vue`), gated in v3 by a hardcoded
 * sysadmin id/email (`accountStore.ts:48-55`) — so no other actor could hold this URL. v4's route
 * carries `requiresSysAdmin`, which refuses anyone else at the guard; that is contract 22 §12's
 * defence in depth, not a shim fallback.
 *
 * ⚠ `data-connectors` and its `tools:` spellings are 20's Workspace › Data Connectors pane — v3
 * redirected all four to `/workspace-settings/workspace/data-connectors` (`WorkspaceSettings.vue`
 * `legacySettingsRedirects` / `legacyGroupedSettingsRedirects`).
 *
 * ⚠ `business context` and its `weekly-digest` path segment are 14's Workspace › Business Context
 * pane — v3's tab `value` was `weekly_digest` (`useWorkspaceSettings.js:192-196`), which
 * `toUrlSegment` turns into `weekly-digest`.
 */
const LEGACY_TAB_NAV_KEYS: Readonly<Record<string, string>> = {
  workspace: 'workspace.basics',
  basics: 'workspace.basics',
  connections: 'connections',
  notifications: 'notifications',
  permissions: 'permissions',
  account: 'account',
  profile: 'profile',
  credits: 'credits',
  subscription: 'credits',
  insights: 'insights.workbench',
  'creative ai': 'creative-ai',
  'creative-ai': 'creative-ai',
  'custom definitions': 'workspace.definitions',
  'custom-definitions': 'workspace.definitions',
  'custom conversions and events': 'workspace.conversions',
  'custom-conversions-and-events': 'workspace.conversions',
  'source/medium': 'workspace.sourceMedium',
  'source-medium': 'workspace.sourceMedium',
  'product customization': 'workspace.product',
  'product-customization': 'workspace.product',
  'negative keywords': 'workspace.negativeKeywords',
  'negative-keywords': 'workspace.negativeKeywords',
  'data connectors': 'workspace.dataConnectors',
  'data-connectors': 'workspace.dataConnectors',
  data_connectors: 'workspace.dataConnectors',
  'tools:data-connectors': 'workspace.dataConnectors',
  'tools:data_connectors': 'workspace.dataConnectors',
  'business context': 'workspace.businessContext',
  'weekly-digest': 'workspace.businessContext',
  weekly_digest: 'workspace.businessContext',
}

/**
 * v3 tabs whose pane left the app altogether. `AI assistants (MCP)` moved to mcp.orphex.co
 * (ORP-27468), and v3 sends its old link to the app's home rather than to another pane — so these
 * never take the first-visible-pane fallback below.
 */
const RETIRED_LEGACY_TABS: ReadonlySet<string> = new Set(['ai assistants (mcp)', 'ai-assistants'])

/** True for a legacy tab token whose pane no longer exists in the app — redirect it home. */
export function isRetiredLegacySettingsTab(tab: string): boolean {
  return RETIRED_LEGACY_TABS.has(tab.trim().toLowerCase())
}

/**
 * Resolve a legacy `?selected_tab` value to the route name of the pane it should open.
 *
 * Two inputs cannot name a pane — an unknown token, and a mapped tab whose module has not
 * registered yet — and both take §14's stated fallback: the user's FIRST VISIBLE pane, which is
 * exactly where `/settings` would have put them.
 *
 * ⚠ That fallback is returned as a real destination rather than as "no redirect", and the
 * difference is not cosmetic. Handing the seam `undefined` still marks the param consumed, so the
 * seam `replace`s to `/settings` to strip it — while `SettingsIndex` is concurrently `replace`ing
 * to the first visible pane. Measured, the strip lands last and drags the user back onto the index,
 * where the transitional host landing renders and nothing redirects again. Naming the destination
 * makes both racers agree on it, and the redirect strips the param on its own (the seam skips the
 * strip pass once the param is gone from the URL).
 *
 * `undefined` therefore means only "the registry is empty" — no pane exists to land on at all, so
 * the caller must not redirect.
 *
 * Visibility is NOT consulted for a MAPPED tab. v3 matched only among `active` tabs, so a hidden
 * tab silently bounced you elsewhere; v4's §12 runs defence in depth instead — the nav hides the
 * entry, the pane degrades, the backend refuses — and ORP-18893's own AC requires a viewer
 * deep-linking a pane to see it read-only rather than be redirected. The FALLBACK does consult it,
 * because landing someone on a pane they cannot see is not a landing.
 */
export function legacySettingsTabRouteName(tab: string): string | undefined {
  const navKey = LEGACY_TAB_NAV_KEYS[tab.trim().toLowerCase()]
  const mapped =
    navKey === undefined
      ? undefined
      : settingsNavEntries().find((entry) => entry.key === navKey)?.routeName
  return mapped ?? orderedVisibleSettingsEntries()[0]?.routeName
}
